import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const sameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const parse = (value: string | null) => { if (!value) return null; const d = new Date(value); return Number.isNaN(d.getTime()) ? null : d; };

/** Local calendar + time picker that stores UTC ISO strings (workspace port of ui/date-time-picker). */
export function DateTimeField({ value, onChange, placeholder, label }: { value: string | null; onChange: (value: string | null) => void; placeholder: string; label: string }) {
  const selected = parse(value);
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [month, setMonth] = useState(() => { const d = selected ?? new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const days = useMemo(() => {
    const offset = month.getDay(), count = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    return Array.from({ length: offset + count }, (_, i) => i < offset ? null : i - offset + 1);
  }, [month]);

  function chooseDay(day: number) {
    const current = selected ?? new Date();
    onChange(new Date(month.getFullYear(), month.getMonth(), day, current.getHours(), current.getMinutes(), 0, 0).toISOString());
  }
  function changeTime(part: "hour" | "minute", raw: string) {
    const next = new Date(selected ?? new Date());
    if (part === "hour") next.setHours(Number(raw)); else next.setMinutes(Number(raw));
    next.setSeconds(0, 0);
    onChange(next.toISOString());
  }
  function toggle() {
    if (!open) { const d = selected ?? new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }
    setOpen(o => !o);
  }

  useEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = trigger.current?.getBoundingClientRect();
      if (!rect) return;
      const width = Math.min(300, window.innerWidth - 16), height = 360;
      setPos({ left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), top: rect.bottom + 6 + height <= window.innerHeight ? rect.bottom + 6 : Math.max(8, rect.top - height - 6) });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => { window.removeEventListener("resize", place); window.removeEventListener("scroll", place, true); };
  }, [open]);
  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return <div className="nw-date">
    <button ref={trigger} type="button" className={cn("nw-date-trigger", !selected && "is-empty")} onClick={toggle} aria-label={label} aria-haspopup="dialog" aria-expanded={open} title={label}>
      <CalendarDays size={14} /><span>{selected ? selected.toLocaleString() : value ? "Invalid date" : placeholder}</span>
    </button>
    {value && <button type="button" className="pw-icon-button nw-date-clear" aria-label={`Clear ${label.toLowerCase()}`} onClick={() => onChange(null)}><X size={14} /></button>}
    {open && createPortal(<dialog ref={dialog} className="nw-calendar" style={pos} onClose={() => setOpen(false)} aria-label={label}>
      <div className="nw-calendar-head">
        <button type="button" className="pw-icon-button" aria-label="Previous month" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}><ChevronLeft size={15} /></button>
        <strong>{MONTHS[month.getMonth()]} {month.getFullYear()}</strong>
        <button type="button" className="pw-icon-button" aria-label="Next month" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}><ChevronRight size={15} /></button>
      </div>
      <div className="nw-calendar-grid nw-calendar-weekdays">{WEEKDAYS.map(d => <span key={d}>{d}</span>)}</div>
      <div className="nw-calendar-grid">{days.map((day, i) => day === null ? <span key={`b${i}`} /> : <button key={day} type="button" onClick={() => chooseDay(day)}
        className={cn(selected && sameDay(selected, new Date(month.getFullYear(), month.getMonth(), day)) && "is-selected", sameDay(new Date(), new Date(month.getFullYear(), month.getMonth(), day)) && "is-today")}
        aria-label={`${MONTHS[month.getMonth()]} ${day}, ${month.getFullYear()}`}>{day}</button>)}</div>
      <div className="nw-calendar-time">
        <select aria-label="Hour" value={selected?.getHours() ?? new Date().getHours()} onChange={e => changeTime("hour", e.target.value)}>{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}</select>
        <span>:</span>
        <select aria-label="Minute" value={selected?.getMinutes() ?? new Date().getMinutes()} onChange={e => changeTime("minute", e.target.value)}>{Array.from({ length: 60 }, (_, m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}</select>
        <small>Local time</small>
      </div>
      <div className="nw-calendar-foot">
        <button type="button" className="pw-button" onClick={() => onChange(null)}>Clear</button>
        <button type="button" className="pw-button pw-button-primary" onClick={() => setOpen(false)}>Done</button>
      </div>
    </dialog>, document.body)}
  </div>;
}
